import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Printer, FileText, Mail, MessageCircle, Download, Share2, X, Send, AlertTriangle, Loader2 } from "lucide-react";
import CustomerEmailGate from "@/components/documents/CustomerEmailGate";
import WhatsAppCustomerGate from "@/components/documents/WhatsAppCustomerGate";
import { waLink } from "@/lib/wa";

const FORMATS = [
  { key: "standard", label: "Full Page / PDF" },
  { key: "thermal80", label: "Receipt 80mm" },
  { key: "thermal58", label: "Receipt 58mm" },
];

// Stage 3 — dedicated, read-only Preview Quote. Renders the EXACT HTML `getHtml(format)`
// returns (the same builder the real Print action uses, via `previewHtml` in lib/print.js)
// in a sandboxed iframe — never opens a print dialog, never sends anything, never
// touches the quotation's status/stock/payments on its own. Print/Email/WhatsApp are
// explicit buttons the user must click; closing this dialog changes nothing.
// Free WhatsApp Click to Chat (Owner direction, 2026-10-25): "WhatsApp Customer" opens a
// wa.me chat to the customer's saved WhatsApp-capable number with a prefilled text message
// only — never a typed-in number, never a claim that the PDF is attached. The PDF download
// below it is a separate, generic action for the cashier to attach manually if they choose.
export default function QuotePreviewDialog({
  open, onOpenChange, quote, isDraft, customerEmail, customerPhone, customerId, onCustomerUpdated,
  getHtml, onPrint, onEmail, getPdfBlob, waMessage, onWhatsAppShared,
}) {
  const [format, setFormat] = useState("standard");
  const [emailOpen, setEmailOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [custEmail, setCustEmail] = useState("");
  const [waNumber, setWaNumber] = useState("");
  const [sending, setSending] = useState(false);
  const [confirmResend, setConfirmResend] = useState(null); // { message } when a 409 asks "send again?"
  const [busyAction, setBusyAction] = useState(null); // "share" | "download" | null — separate so the spinner only shows on the button actually clicked
  const pdfBlobRef = useRef(null); // pre-fetched PDF, so the click handler can call
                                    // navigator.share synchronously inside the user gesture
  const iframeRef = useRef(null);

  const canShare = !isDraft && !!quote?.id;
  const html = open ? (getHtml?.(format) || "") : "";

  // Pre-fetch the PDF as soon as a shareable quote is previewed, rather than on click.
  // iOS Safari in particular requires navigator.share to run inside (or very close to)
  // the click's user-activation window — awaiting a network fetch first can make it
  // silently reject. Pre-fetching means the click handler only needs the already-
  // resolved blob, not a fresh network round trip.
  useEffect(() => {
    pdfBlobRef.current = null;
    if (open && canShare && getPdfBlob) {
      getPdfBlob().then((b) => { pdfBlobRef.current = b; }).catch(() => { pdfBlobRef.current = null; });
    }
  }, [open, canShare, quote?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const resize = () => {
    try {
      const doc = iframeRef.current?.contentWindow?.document;
      if (doc) iframeRef.current.style.height = `${doc.documentElement.scrollHeight + 24}px`;
    } catch { /* cross-origin never happens for srcDoc, defensive only */ }
  };

  const openEmail = () => { setCustEmail(customerEmail || ""); setConfirmResend(null); setWaOpen(false); setEmailOpen(true); };
  const openWa = () => { setWaNumber(customerPhone || ""); setEmailOpen(false); setWaOpen(true); };

  const doSendEmail = async (confirm = false) => {
    if (!custEmail) return;
    setSending(true);
    try {
      await onEmail(confirm);
      toast.success(`Quotation emailed to ${custEmail}`);
      setEmailOpen(false); setConfirmResend(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.already_recently_sent) { setConfirmResend({ message: detail.message }); }
      else { toast.error(apiError(detail)); }
    } finally { setSending(false); }
  };

  // Click to Chat — opens WhatsApp with the message already typed in; the cashier still has
  // to review it and tap Send inside WhatsApp themselves. Never assumes that counts as sent.
  const openWhatsAppCustomer = () => {
    const link = waLink(waNumber, waMessage);
    if (!link) { toast.error("Saved number looks invalid — use Edit Customer to fix it."); return; }
    window.open(link, "_blank", "noopener");
    onWhatsAppShared?.("click_to_chat");
  };

  const fetchQuotePdf = async () => pdfBlobRef.current || await getPdfBlob();

  // Share Document — hands the real PDF to the OS's own share sheet so the cashier can pick
  // WhatsApp, Outlook, Drive, etc. themselves with the file genuinely attached. When the
  // browser/device doesn't support file sharing at all, says so plainly rather than silently
  // substituting a download under this button's name.
  const shareDocument = async () => {
    if (!canShare || busyAction) return;
    setBusyAction("share");
    let blob;
    try { blob = await fetchQuotePdf(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not prepare the PDF."); setBusyAction(null); return; }
    const fileName = `Quotation-${quote.quote_number || "draft"}.pdf`.replace(/[\\/]/g, "-");
    const file = new File([blob], fileName, { type: "application/pdf" });
    const canNativeShare = typeof navigator !== "undefined" && !!navigator.share && !!navigator.canShare
      && navigator.canShare({ files: [file] });
    if (!canNativeShare) {
      toast.message("Sharing isn't supported in this browser", { description: "Use Download PDF instead." });
      setBusyAction(null);
      return;
    }
    try {
      await navigator.share({ files: [file], title: `Quotation ${quote.quote_number}` });
      toast.success("Shared via your device.");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not share the file — try Download PDF instead.");
    } finally { setBusyAction(null); }
  };

  // Download PDF — a plain, always-available file download. Deliberately never attempts
  // native share; Share Document above is the only path that hands the file to another app.
  const downloadPdf = async () => {
    if (!canShare || busyAction) return;
    setBusyAction("download");
    try {
      const blob = await fetchQuotePdf();
      const fileName = `Quotation-${quote.quote_number || "draft"}.pdf`.replace(/[\\/]/g, "-");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fileName; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not prepare the PDF.");
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setEmailOpen(false); setWaOpen(false); setConfirmResend(null); setFormat("standard"); } }}>
      <DialogContent className="max-w-[900px] w-[94vw] h-[88vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="quote-preview-dialog">
        <DialogHeader className="px-5 py-3 border-b border-border shrink-0 flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <DialogTitle className="font-head text-base">Quotation Preview</DialogTitle>
            {isDraft && <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30" data-testid="quote-preview-draft-badge">UNSAVED DRAFT — not yet saved</Badge>}
          </div>
          <DialogDescription className="sr-only">Read-only preview of this quotation. No changes are made while previewing.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1.5 px-5 py-2 border-b border-border shrink-0 bg-secondary/40">
          {FORMATS.map((f) => (
            <Button key={f.key} size="sm" variant={format === f.key ? "default" : "outline"}
              className={`h-7 px-2.5 text-xs ${format === f.key ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
              onClick={() => setFormat(f.key)} data-testid={`quote-preview-format-${f.key}`}>{f.label}</Button>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto bg-slate-200 dark:bg-slate-900 p-4 flex justify-center" data-testid="quote-preview-canvas">
          <iframe ref={iframeRef} title="Quotation preview" srcDoc={html} onLoad={resize} sandbox="allow-same-origin"
            data-testid="quote-preview-frame"
            className={`bg-white shadow-lg ${format === "standard" ? "w-full max-w-[820px]" : format === "thermal58" ? "w-[230px]" : "w-[320px]"}`}
            style={{ border: 0, minHeight: 200 }} />
        </div>

        {emailOpen && (
          <div className="px-5 py-3 border-t border-border shrink-0 space-y-2 bg-secondary/40" data-testid="quote-preview-email-panel">
            {confirmResend ? (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-sm space-y-2" data-testid="quote-email-resend-confirm">
                <p className="flex items-start gap-1.5 text-amber-700 dark:text-amber-400"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{confirmResend.message}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => setConfirmResend(null)} data-testid="quote-email-resend-cancel">Cancel</Button>
                  <Button size="sm" className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" disabled={sending} onClick={() => doSendEmail(true)} data-testid="quote-email-resend-confirm-btn">Send Anyway</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {!customerId ? (
                  <div className="rounded-lg border border-dashed border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="quote-no-customer-linked-notice">
                    <p className="flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400"><AlertTriangle className="w-4 h-4" /> No customer linked</p>
                    <p className="text-xs text-muted-foreground mt-1">Attach a customer to this quotation first — Print, Share Document and Download PDF are still available.</p>
                  </div>
                ) : (
                  <CustomerEmailGate customerId={customerId} email={custEmail} onEmailResolved={setCustEmail} onCustomerUpdated={onCustomerUpdated}>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className="text-xs text-muted-foreground">Sending quotation to</label>
                        <div className="h-10 px-3 rounded-md border border-border bg-secondary/40 flex items-center text-sm truncate mt-1" data-testid="quote-email-readonly">{custEmail}</div>
                      </div>
                      <Button variant="outline" onClick={() => setEmailOpen(false)} data-testid="quote-email-cancel-btn">Cancel</Button>
                      <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={sending} onClick={() => doSendEmail(false)} data-testid="quote-email-confirm-send-btn">
                        <Send className="w-4 h-4 mr-1.5" /> {sending ? "Sending…" : "Send"}
                      </Button>
                    </div>
                  </CustomerEmailGate>
                )}
              </div>
            )}
          </div>
        )}

        {waOpen && (
          <div className="px-5 py-3 border-t border-border shrink-0 space-y-2 bg-secondary/40" data-testid="quote-preview-whatsapp-panel">
            {!customerId ? (
              <div className="rounded-lg border border-dashed border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="quote-no-customer-linked-wa-notice">
                <p className="flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400"><AlertTriangle className="w-4 h-4" /> No customer linked</p>
                <p className="text-xs text-muted-foreground mt-1">Attach a customer to this quotation first — Print, Share Document and Download PDF are still available.</p>
              </div>
            ) : (
              <WhatsAppCustomerGate customerId={customerId} number={waNumber} onNumberResolved={setWaNumber} onCustomerUpdated={onCustomerUpdated}>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="text-xs text-muted-foreground">WhatsApp Customer</label>
                    <div className="h-10 px-3 rounded-md border border-border bg-secondary/40 flex items-center text-sm truncate mt-1" data-testid="quote-whatsapp-readonly">{waNumber}</div>
                  </div>
                  <Button variant="outline" onClick={() => setWaOpen(false)} data-testid="quote-whatsapp-cancel-btn">Cancel</Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openWhatsAppCustomer} data-testid="quote-whatsapp-open-btn">
                    <MessageCircle className="w-4 h-4 mr-1.5" /> Open WhatsApp
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Opens WhatsApp with the message ready — review it and tap Send yourself.</p>
              </WhatsAppCustomerGate>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-t border-border shrink-0">
          <Button variant="outline" onClick={() => onPrint?.(format === "standard" ? "thermal80" : format)} data-testid="quote-preview-print-thermal">
            <Printer className="w-4 h-4 mr-1.5" /> Print Thermal Receipt
          </Button>
          <Button variant="outline" onClick={() => onPrint?.("standard")} data-testid="quote-preview-print-fullpage">
            <FileText className="w-4 h-4 mr-1.5" /> Print Full Page / Save PDF
          </Button>
          <Button variant="outline" disabled={!canShare} title={!canShare ? "Save the quotation first to email it" : ""}
            onClick={openEmail} data-testid="quote-preview-email-btn">
            <Mail className="w-4 h-4 mr-1.5" /> Email Quotation
          </Button>
          <Button variant="outline" disabled={!canShare} className="text-emerald-600 border-emerald-500/40"
            title={!canShare ? "Save the quotation first to share it" : ""}
            onClick={openWa} data-testid="quote-preview-whatsapp-btn">
            <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp Customer
          </Button>
          <Button variant="outline" disabled={!canShare || !!busyAction}
            title={!canShare ? "Save the quotation first to share it" : ""}
            onClick={shareDocument} data-testid="quote-preview-share-document-btn">
            {busyAction === "share" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Share2 className="w-4 h-4 mr-1.5" />} Share Document
          </Button>
          <Button variant="outline" disabled={!canShare || !!busyAction}
            title={!canShare ? "Save the quotation first to share it" : ""}
            onClick={downloadPdf} data-testid="quote-preview-pdf-btn">
            {busyAction === "download" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Download PDF
          </Button>
          <Button variant="ghost" className="ml-auto" onClick={() => onOpenChange(false)} data-testid="quote-preview-close-btn">
            <X className="w-4 h-4 mr-1.5" /> Close Preview
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
